import _ from "lodash";

import { IErpOrder } from "containers/erp/types";
import { updateItemInArray } from "containers/workflows/subcategories/jobs/Jobscreen/notification/EmailNotificationConfigForm/utils/updateItemInArray";
import { IAction } from "redux/store/model";
import { updateItemsInArray } from "utils/reducerUtils";

import {
  deleteOrderLineSuccessActionCreator,
  FetchAllOrdersSuccessAction,
  IConfirmOrderSuccessAction,
  IEditDeliveryOrderSuccessAction,
  IFetchOrderSuccessAction,
  IUpdateOrderStatusSuccessAction,
  updateOrderLineSuccessActionCreator,
} from "./actionCreators";
import * as types from "./actionTypes";
import initialState from "./initialState";

export interface IErpOrdersState {
  orders: IErpOrder[];
  isLoading: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isDeletingOrderLines: boolean;
  isUpdatingOrderLines: boolean;
  isEditingDelivery: boolean;
  count: number;
}

export default function reducer(state = initialState, action: IAction) {
  switch (action.type) {
    case types.FETCH_ALL_ERP_ORDERS_BEGIN:
    case types.FETCH_ERP_ORDER_DETAIL_BEGIN: {
      return {
        ...state,
        isLoading: true,
      };
    }
    case types.FETCH_ALL_ERP_ORDERS_FAILURE:
    case types.FETCH_ERP_ORDER_DETAIL_FAILURE: {
      return {
        ...state,
        isLoading: false,
      };
    }
    case types.FETCH_ALL_ERP_ORDERS_SUCCESS: {
      const { orders, count, append } = action as FetchAllOrdersSuccessAction;

      let newOrders: IErpOrder[] = orders;

      if (append) {
        newOrders = _.concat(state.orders, orders);
      }

      return {
        ...state,
        isLoading: false,
        orders: newOrders,
        count,
      };
    }
    case types.UPDATE_ERP_ORDER_STATUS_BEGIN: {
      return {
        ...state,
        isUpdating: true,
      };
    }
    case types.UPDATE_ERP_ORDER_STATUS_FAILURE: {
      return {
        ...state,
        isUpdating: false,
      };
    }
    case types.UPDATE_ERP_ORDER_STATUS_SUCCESS: {
      const { orders } = action as IUpdateOrderStatusSuccessAction;

      const newOrders = _.forEach(state.orders, (order) => {
        const updatedOrder = _.find(orders, { id: order.id });
        if (updatedOrder) {
          order.status = updatedOrder.status;
        }
      });

      const updatedOrder = updateItemsInArray(state.orders, newOrders);
      return {
        ...state,
        isUpdating: false,
        orders: updatedOrder,
      };
    }
    case types.FETCH_SFA_DATA_SUCCESS: {
      const { dataKey, data } = action as any;
      return {
        ...state,
        isUpdating: false,
        [dataKey]: data,
      };
    }
    case types.DELETE_ORDER_LINE_SUCCESS: {
      const { order, lineId } = action as ReturnType<
        typeof deleteOrderLineSuccessActionCreator
      >;
      // NOTE: ideally order object should be found by ID in the store
      // But currently, state.orders can be empty, if we loaded the order details screen directly...
      // A better approach would be to update the store orders when fetching the single order
      const lines = _.reject(order?.lines, { id: lineId });
      const newOrder = { ...order, lines };
      const newOrders = updateItemInArray(state.orders, newOrder, {
        id: newOrder.id,
      });

      return {
        ...state,
        isDeletingOrderLines: false,
        orders: newOrders,
      };
    }
    case types.DELETE_ORDER_LINE_BEGIN: {
      return {
        ...state,
        isDeletingOrderLines: true,
      };
    }
    case types.DELETE_ORDER_LINE_FAILURE: {
      return {
        ...state,
        isDeletingOrderLines: false,
      };
    }
    case types.UPDATE_ORDER_LINE_BEGIN: {
      return {
        ...state,
        isUpdatingOrderLines: true,
      };
    }
    case types.UPDATE_ORDER_LINE_SUCCESS: {
      const { order, orderLine } = action as ReturnType<
        typeof updateOrderLineSuccessActionCreator
      >;
      // NOTE: ideally order object should be found by ID in the store
      // But currently, state.orders can be empty, if we loaded the order details screen directly...
      // A better approach would be to update the store orders when fetching the single order
      const lines = updateItemInArray(order?.lines, orderLine, {
        id: orderLine.id,
      });

      const newOrder = { ...order, lines };

      const newOrders = updateItemInArray(state.orders, newOrder, {
        id: newOrder.id,
      });
      return {
        ...state,
        isUpdatingOrderLines: false,
        orders: newOrders,
      };
    }
    case types.UPDATE_ORDER_LINE_FAILURE: {
      return {
        ...state,
        isUpdatingOrderLines: false,
      };
    }
    case types.CONFIRM_ORDER_BEGIN: {
      return {
        ...state,
        isConfirming: true,
      };
    }
    case types.CONFIRM_ORDER_SUCCESS: {
      const { order } = action as IConfirmOrderSuccessAction;

      let newOrders = state.orders;

      const orderInState = _.find(state.orders, { id: order.id });

      newOrders = updateItemInArray(
        newOrders,
        { ...orderInState, ...order },
        {
          id: order.id,
        },
      );

      return {
        ...state,
        isConfirming: false,
        orders: newOrders,
      };
    }
    case types.CONFIRM_ORDER_FAILURE: {
      return {
        ...state,
        isConfirming: false,
      };
    }
    case types.EDIT_DELIVERY_ORDER_BEGIN: {
      return {
        ...state,
        isEditingDelivery: true,
      };
    }
    case types.EDIT_DELIVERY_ORDER_SUCCESS: {
      const { orderId, deliveryOrder } =
        action as IEditDeliveryOrderSuccessAction;

      // Find the order and update its delivery_order property
      const newOrders = state.orders.map((order) => {
        if (order.id === orderId) {
          return {
            ...order,
            delivery_order: deliveryOrder,
          };
        }
        return order;
      });

      return {
        ...state,
        isEditingDelivery: false,
        orders: newOrders,
      };
    }
    case types.EDIT_DELIVERY_ORDER_FAILURE: {
      return {
        ...state,
        isEditingDelivery: false,
      };
    }
    case types.FETCH_ERP_ORDER_DETAIL_SUCCESS: {
      const { order } = action as IFetchOrderSuccessAction;

      const existingOrderIndex = _.findIndex(state.orders, { id: order.id });

      const newOrders =
        existingOrderIndex >= 0
          ? updateItemInArray(state.orders, order, { id: order.id })
          : [order, ...state.orders];

      return {
        ...state,
        isLoading: false,
        orders: newOrders,
      };
    }
    default: {
      return state;
    }
  }
}
